import { useCallback, useEffect, useRef } from 'react';
import { cloudStorage } from '@telegram-apps/sdk';
import { useAppDispatch, useAppSelector } from '../redux';
import {
  toggleTraffic,
  toggleDpsMarkers,
  toggleWebcamsMarkers,
  toggleCleanMarkers,
  toggleDtpMarkers,
  setMapFiltersFromStorage,
  setFiltersLoaded
} from '../../store/slices/mapSlice';
import { selectMapFilters } from '../../store/selectors/mapSelectors';

const STORAGE_KEY = 'map_filters';
const DEBOUNCE_DELAY = 500;

interface MapFiltersState {
  isTrafficVisible: boolean;
  isDpsMarkersVisible: boolean;
  isWebcamsMarkersVisible: boolean;
  isCleanMarkersVisible: boolean;
  isDtpMarkersVisible: boolean;
}

export const useMapFiltersStorage = () => {
  const dispatch = useAppDispatch();
  // Используем мемоизированный селектор для предотвращения лишних ре-рендеров
  const filters = useAppSelector(selectMapFilters);

  const saveTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isInitializedRef = useRef(false);

  // Загрузка настроек из cloudStorage при монтировании
  useEffect(() => {
    const loadFilters = async () => {
      if (isInitializedRef.current) return;

      try {
        if (cloudStorage.isSupported() && cloudStorage.getItem.isAvailable()) {
          const storedData = await cloudStorage.getItem(STORAGE_KEY);

          if (storedData) {
            const savedFilters: MapFiltersState = JSON.parse(storedData);
            dispatch(setMapFiltersFromStorage(savedFilters));
            console.log('✅ Настройки фильтров загружены из cloudStorage:', savedFilters);
          }
        }
      } catch (error) {
        console.warn('⚠️ Ошибка загрузки настроек фильтров:', error);
      } finally {
        isInitializedRef.current = true;
        // Устанавливаем флаг загрузки даже если нет сохраненных данных
        dispatch(setFiltersLoaded(true));
      }
    };

    loadFilters();
  }, [dispatch]);

  // Сохранение настроек в cloudStorage с debounce
  useEffect(() => {
    if (!isInitializedRef.current) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      try {
        if (cloudStorage.isSupported() && cloudStorage.setItem.isAvailable()) {
          const dataToSave = JSON.stringify(filters);
          await cloudStorage.setItem(STORAGE_KEY, dataToSave);
          console.log('💾 Настройки фильтров сохранены в cloudStorage');
        }
      } catch (error) {
        console.warn('⚠️ Ошибка сохранения настроек фильтров:', error);
      }
    }, DEBOUNCE_DELAY);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [filters]);

  const handleTrafficToggle = useCallback(() => {
    dispatch(toggleTraffic());
  }, [dispatch]);

  const handleDpsToggle = useCallback(() => {
    dispatch(toggleDpsMarkers());
  }, [dispatch]);

  const handleWebcamsToggle = useCallback(() => {
    dispatch(toggleWebcamsMarkers());
  }, [dispatch]);

  const handleCleanToggle = useCallback(() => {
    dispatch(toggleCleanMarkers());
  }, [dispatch]);

  const handleDtpToggle = useCallback(() => {
    dispatch(toggleDtpMarkers());
  }, [dispatch]);

  return {
    filters,
    handlers: {
      handleTrafficToggle,
      handleDpsToggle,
      handleWebcamsToggle,
      handleCleanToggle,
      handleDtpToggle,
    },
  };
};
